import React, { useMemo } from 'react'
const { FlatList } = XPReactNative
import { color, useParamLink, useSearchParams } from '@my/util'
import { Image, ScrollView, SimpleText, XStack, YStack, ActionButton, Avatar, XPReactNative, XPImageProps as ImageProps } from '@my/ui'

import assets from '../../../assets/images'
import { HomeMainHeading, GradientButton } from '../../../components/index'
import { ProfileComponent } from '../../../components/common/ProfileDetail'
import { shadowGenerator } from '../../../utils/commonStyles'
import { ProfileHeaderProps } from './types'
import { userProfiles } from '../helper'

// ProfileHeader Component to display basic profile information
const ProfileHeader: React.FC<ProfileHeaderProps> = (props) => {
  const { name, location, phone, gender, age, religion, image, email } = props
  return (
    <YStack
      padding={16}
      gap={30}
      justifyContent="center"
      borderRadius={10}
      {...shadowGenerator(0.3)}
      bg={color.white}
    >
      <ProfileComponent name={name} image={image} />

      <XStack gap={20} flexWrap="wrap">
        <InfoRow label={email} icon={assets.emailLogo} />
        <InfoRow label={location} icon={assets.AdvisorLocation} />
        <InfoRow label={phone} icon={assets.AdvisorMobile} />
        <InfoRow label={`${gender}, ${age}`} icon={assets.AdvisorGender} />
        <InfoRow label={religion} icon={assets.AdvisorReligion} />
      </XStack>
    </YStack>
  )
}

// Helper component for displaying info rows in the profile header
const InfoRow: React.FC<{ label: string; icon: ImageProps }> = ({ label, icon }) => (
  <XStack alignItems="center" gap={15} minWidth={280}>
    <GradientButton
      image={{
        src: icon,
      }}
      btnHeight={30}
      btnWidth={30}
    />
    <SimpleText fontSize={15} fontWeight={'400'} lineHeight={20} color={color.lightGrey}>
      {label}
    </SimpleText>
  </XStack>
)

// Reusable ProfileSection component for different profile details
const ProfileSection: React.FC<{ title: string; content: string; icon?: ImageProps }> = ({
  title,
  content,
  icon,
}) => (
  <XStack gap={10}>
    {icon && <Image src={icon} resizeMode="contain" style={{height: 25, width:25}}/>}
    <YStack gap={7} flex={1}>
      <SimpleText fontSize={15} lineHeight={22} color={color.tzaGreen}>
        {title}
      </SimpleText>
      <SimpleText fontSize={17} lineHeight={20} color={color.lightGrey}>
        {content}
      </SimpleText>
    </YStack>
  </XStack>
)

// UserProfile component for each user
const UserProfile = ({ user }) => {
  return (
    <ActionButton
      unstyled
      alignItems="center"
      width={120}
      paddingVertical={10}
      //{...useParamLink('advisorDetails', { ...user })}
    >
      <Avatar size={80} circular>
        <Avatar.Image src={user.image || assets.advisorDummy} />
      </Avatar>
      <SimpleText fontWeight="700" fontSize={13} lineHeight={17} color="#000" marginTop={8}>
        {user.name}
      </SimpleText>
      <SimpleText fontWeight="400" fontSize={8} lineHeight={11} color="#555">
        lorem ipsum lorem
      </SimpleText>
    </ActionButton>
  )
}

//Main Component
const AdvisorDetail: React.FC = () => {
  const { id } = useSearchParams()

  const profileData: any = useMemo(() => {
    return userProfiles.find((item) => item.id == id)
  }, [id])

  return (
    <ScrollView bg={color.white}>
      {/* Web Header with HomeMainHeading, hidden on native */}
      <HomeMainHeading image={assets.advisor1} type="Details" displayNative />
      <YStack gap={30} padding={16}>
        {/* Profile Header */}
        <ProfileHeader {...profileData} />
        <YStack borderRadius={10} padding={16} gap={30} {...shadowGenerator(0.3)} bg={color.white}>
          {/* Education */}
          <ProfileSection
            title="Level of Education"
            content={profileData?.education?.level}
            icon={assets.AdvisorEducation}
          />

          {/* Expertise */}
          <ProfileSection
            title="Expertise"
            content={profileData?.expertise}
            icon={assets.AdvisorExperties}
          />

          {/* Certificate */}
          <ProfileSection
            title="Certificate"
            content={`${profileData?.certificate?.name}(${profileData?.certificate?.abbreviation})`}
            icon={assets.AdvisorCertificates}
          />
        </YStack>
        <YStack borderRadius={10} padding={16} gap={30} {...shadowGenerator(0.3)} bg={color.white}>
          {/* About */}
          <ProfileSection title="About" content={profileData?.about} />
        </YStack>
        <XStack flexWrap="wrap" gap={30}>
          <YStack
            borderRadius={10}
            maxWidth={500}
            minWidth={300}
            flex={1}
            padding={16}
            gap={30}
            {...shadowGenerator(0.3)}
            bg={color.white}
          >
            {/* Available */}
            <ProfileSection title="Available Dates" content={'Today, 3 PM - 4 PM'} />
          </YStack>
          <YStack
            borderRadius={10}
            maxWidth={500}
            minWidth={300}
            flex={1}
            padding={16}
            gap={30}
            {...shadowGenerator(0.3)}
            bg={color.white}
          >
            {/* Fees */}
            <ProfileSection title="Fees" content={'$100/hr'} />
          </YStack>
        </XStack>

        <ActionButton
          height={60}
          borderRadius={10}
          bg={color.lightGreen}
          borderColor={color.lightGreen}
        >
          <SimpleText color={color.white} fontWeight={'700'} fontSize={18}>
            Confirm
          </SimpleText>
        </ActionButton>
      </YStack>

      {/* Other Advisor Header  */}
      <SimpleText
        fontSize={18}
        paddingHorizontal={20}
        fontWeight={'700'}
        lineHeight={25}
        color={color.tzaGreen}
      >
        Other Advisors
      </SimpleText>

      {/* List of other similar advisors  */}
      <FlatList
        horizontal
        data={userProfiles}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => <UserProfile user={item} />}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingBottom: 20,
        }}
        showsHorizontalScrollIndicator={false}
      />
    </ScrollView>
  )
}

export { AdvisorDetail }
